// Sound and haptics for the key moments of a battle. Every sound is made on the phone with Web Audio,
// so there is nothing to download and nothing to wait for. Each moment pairs a sound with Telegram's
// haptic feedback; the sound can be muted, the haptics follow the phone's own settings.
import { tg } from "./telegram.js";

export type Moment = "tap" | "matched" | "lead" | "tick" | "lastTick" | "heartbeat" | "win" | "lose" | "draw";

const MUTE_KEY = "dd.muted";
let ctx: AudioContext | null = null;
let muted = readMuted();

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

export const isMuted = () => muted;

export function setMuted(on: boolean): void {
  muted = on;
  try {
    localStorage.setItem(MUTE_KEY, on ? "1" : "0");
  } catch {
    // Private mode: the choice lasts until the app closes.
  }
}

/**
 * Phones only allow sound after a tap, so the first tap anywhere wakes the audio. "Ambient" keeps
 * DegenDuel quiet when the phone is on silent and lets the player's music keep playing.
 */
export function initSound(): void {
  const nav = navigator as Navigator & { audioSession?: { type: string } };
  if (nav.audioSession) nav.audioSession.type = "ambient";
  const wake = () => void audio()?.resume();
  window.addEventListener("pointerdown", wake, { passive: true });
}

function audio(): AudioContext | null {
  if (ctx) return ctx;
  const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  return ctx;
}

/** One note: a frequency that can slide, with a quick attack and a decay. */
function note(c: AudioContext, at: number, o: { freq: number; to?: number; len: number; vol?: number; type?: OscillatorType }): void {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = o.type ?? "sine";
  osc.frequency.setValueAtTime(o.freq, at);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, at + o.len);
  const vol = o.vol ?? 0.2;
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(vol, at + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + o.len);
  osc.connect(gain).connect(c.destination);
  osc.start(at);
  osc.stop(at + o.len + 0.02);
}

/** A short burst of noise, for the clash when two players meet. */
function noise(c: AudioContext, at: number, len: number, vol: number): void {
  const buf = c.createBuffer(1, Math.ceil(c.sampleRate * len), c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = c.createBufferSource();
  const filter = c.createBiquadFilter();
  const gain = c.createGain();
  src.buffer = buf;
  filter.type = "bandpass";
  filter.frequency.setValueAtTime(2400, at);
  filter.frequency.exponentialRampToValueAtTime(400, at + len);
  gain.gain.setValueAtTime(vol, at);
  src.connect(filter).connect(gain).connect(c.destination);
  src.start(at);
}

const SOUNDS: Record<Moment, (c: AudioContext, t: number) => void> = {
  // Long or Short tapped: a firm click.
  tap: (c, t) => note(c, t, { freq: 900, to: 500, len: 0.06, vol: 0.18, type: "square" }),
  // MATCHED: a clash, then a rising two-note hit.
  matched: (c, t) => {
    noise(c, t, 0.25, 0.35);
    note(c, t + 0.05, { freq: 330, len: 0.18, vol: 0.22, type: "sawtooth" });
    note(c, t + 0.16, { freq: 660, len: 0.3, vol: 0.22, type: "sawtooth" });
  },
  // The lead changes hands: a quick blip up.
  lead: (c, t) => note(c, t, { freq: 520, to: 880, len: 0.09, vol: 0.12, type: "triangle" }),
  // Seconds 10 to 4: a clock tick.
  tick: (c, t) => note(c, t, { freq: 1500, len: 0.03, vol: 0.14, type: "square" }),
  // Seconds 3, 2, 1: a higher, harder tick.
  lastTick: (c, t) => note(c, t, { freq: 2000, len: 0.05, vol: 0.2, type: "square" }),
  // A close battle in the last 10 seconds: a low double thump in place of the tick.
  heartbeat: (c, t) => {
    note(c, t, { freq: 70, to: 45, len: 0.14, vol: 0.5 });
    note(c, t + 0.18, { freq: 60, to: 40, len: 0.16, vol: 0.4 });
  },
  // Win: a bright rising run that lands on a chord.
  win: (c, t) => {
    [523, 659, 784, 1047].forEach((f, i) => note(c, t + i * 0.07, { freq: f, len: 0.22, vol: 0.16, type: "triangle" }));
    [1047, 1319, 1568].forEach((f) => note(c, t + 0.3, { freq: f, len: 0.6, vol: 0.1, type: "triangle" }));
  },
  // Loss: a falling "womp".
  lose: (c, t) => {
    note(c, t, { freq: 300, to: 220, len: 0.25, vol: 0.2, type: "sawtooth" });
    note(c, t + 0.22, { freq: 220, to: 110, len: 0.45, vol: 0.2, type: "sawtooth" });
  },
  // Draw: two flat, even notes.
  draw: (c, t) => {
    note(c, t, { freq: 440, len: 0.18, vol: 0.14, type: "triangle" });
    note(c, t + 0.2, { freq: 440, len: 0.18, vol: 0.14, type: "triangle" });
  },
};

const HAPTICS: Record<Moment, () => void> = {
  tap: () => tg?.HapticFeedback?.impactOccurred("medium"),
  matched: () => tg?.HapticFeedback?.notificationOccurred("success"),
  lead: () => tg?.HapticFeedback?.impactOccurred("soft"),
  tick: () => tg?.HapticFeedback?.impactOccurred("light"),
  lastTick: () => tg?.HapticFeedback?.impactOccurred("heavy"),
  heartbeat: () => {
    tg?.HapticFeedback?.impactOccurred("heavy");
    setTimeout(() => tg?.HapticFeedback?.impactOccurred("medium"), 180);
  },
  win: () => tg?.HapticFeedback?.notificationOccurred("success"),
  lose: () => tg?.HapticFeedback?.notificationOccurred("error"),
  draw: () => tg?.HapticFeedback?.notificationOccurred("warning"),
};

/** Plays a moment: the haptic always, the sound unless muted. Never throws. */
export function play(m: Moment): void {
  try {
    HAPTICS[m]();
    if (muted) return;
    const c = audio();
    if (!c) return;
    if (c.state === "suspended") void c.resume();
    SOUNDS[m](c, c.currentTime + 0.01);
  } catch {
    // Sound is a nice-to-have: a phone that cannot play it must never break the battle.
  }
}

/** Sound lab only: what the phone's audio is doing, to find out why a phone stays silent. */
export function audioStatus(): string {
  const nav = navigator as Navigator & { audioSession?: { type: string } };
  const c = audio();
  return `audio ${c ? c.state : "not supported"} · session ${nav.audioSession?.type ?? "none"} · ${muted ? "muted" : "not muted"}`;
}

/** Sound lab only: lets sound play even with the iPhone silent switch on, to see if that is the cause. */
export function ignoreSilentSwitch(): void {
  const nav = navigator as Navigator & { audioSession?: { type: string } };
  if (nav.audioSession) nav.audioSession.type = "playback";
}

/** Sound lab only: a loud beep through a plain audio element instead of Web Audio. */
export function elementBeep(): Promise<void> {
  const rate = 22_050;
  const n = Math.floor(rate * 0.4);
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const str = (o: number, t: string) => [...t].forEach((ch, i) => v.setUint8(o + i, ch.charCodeAt(0)));
  str(0, "RIFF"); v.setUint32(4, 36 + n * 2, true); str(8, "WAVEfmt "); v.setUint32(16, 16, true);
  v.setUint16(20, 1, true); v.setUint16(22, 1, true); v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true);
  v.setUint16(32, 2, true); v.setUint16(34, 16, true); str(36, "data"); v.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) v.setInt16(44 + i * 2, Math.sin((2 * Math.PI * 880 * i) / rate) * 0.8 * 32767 * (1 - i / n), true);
  return new Audio(URL.createObjectURL(new Blob([buf], { type: "audio/wav" }))).play();
}
